import {workflowStyles as styles, workflowStyles} from "./unified-card-container.css";
import {cx, formatMsAsSec, TooltipForChild} from "@cdx/common";
import {areChildCardsIncomplete, getChildCards, isHeroCard} from "../workflows/workflow-utils";
import {hasPermissionToGuardCard} from "../../lib/permissions";
import {colorThemes} from "@cdx/ds/css/themes/color-overwrites.css";
import {ButtonArea, WorkflowButton} from "./WorkflowButtonArea";
import {Card, CardId} from "../../cdx-models/Card";
import {Root} from "../../cdx-models/Root";
import {FocusProps} from "./useFocusMode";
import {CoverInfo} from "../../components/Card/card-cover-utils";
import {
  Col,
  DSButton,
  DSIconBlocked,
  DSIconButton,
  DSIconCheck,
  DSIconStopWatchPlay,
  DSIconCrown,
  DSIconFocusMode,
  DSIconPlay,
  DSIconReview,
  DSIconSnoozing,
  Row,
  Text,
  DSIconMisc,
} from "@cdx/ds";
import useMutation from "../../lib/hooks/useMutation";
import {pluralize} from "../../lib/utils";
import Upvotes from "./UpvoteButton";
import {PublicCardButton} from "./CardIdButton";
import {api} from "../../lib/api";
import {getStatusForCard} from "../../lib/card-status-utils";
import {performDoneAction} from "./performDoneAction";
import DSAvatar from "../../components/DSAvatar/DSAvatar";
import {
  getCurrentTimeTrackingSegmentsForCard,
  getSegmentDuration,
} from "../time-tracking/time-tracking-utils";
import {TimeTrackingSegment} from "../../cdx-models/TimeTrackingSegment";
import {cardStatusVariants} from "@cdx/ds/components/DSCard/DSCardTheme.css";
import AnimatedStopWatchIcon from "./AnimatedStopWatchIcon";
import {checkIfPresent} from "../../lib/mate/mate-utils";
import useFeatureFlag, {FEATURE_FLAGS} from "../../components/useFeatureFlag";
import confirm from "../../lib/confirm";
import {useHistory} from "react-router";

type ParentCardBadgeProps = {
  card: Card;
  mayGuardCard: boolean;
  canModify: boolean | null;
  onCardDone?: () => Promise<unknown>;
  childCards: Card[];
};
const ParentDoneButton = (props: ParentCardBadgeProps) => {
  const {card, mayGuardCard, canModify, onCardDone, childCards} = props;
  const cardId = card.$meta.get("cardId", null) as CardId;
  const handleDone = () => {
    if (card.status === "done") {
      return api.mutate.cards.update({id: cardId, status: "not_started"});
    } else {
      return performDoneAction({cardIds: [cardId], onCardDone});
    }
  };

  const getDisableReason = () => {
    if (!mayGuardCard) return "Only Guardians may set a card as done.";
    if (card.status === "done") return null;
    if (areChildCardsIncomplete(childCards)) {
      return "Finish all sub cards before completing this card";
    }
    return null;
  };
  const disableReason = getDisableReason();

  return (
    <WorkflowButton
      icon={<DSIconCheck />}
      status="done"
      active={card.status === "done"}
      tooltip={
        disableReason ||
        (card.status === "done"
          ? "Work not complete? Click again to undo it."
          : "Mark Hero Card as done.")
      }
      disabled={Boolean(disableReason)}
      onClick={handleDone}
      canModify={canModify}
    />
  );
};

const ParentCardBadge = ({
  childCardCount,
  onOpenChildCards,
}: {
  childCardCount: number;
  onOpenChildCards: () => void;
}) => (
  <DSIconButton
    icon={<DSIconCrown />}
    label={pluralize(childCardCount, "Sub Card")}
    onClick={onOpenChildCards}
    variant="secondary"
  />
);

const UnarchiveButton = ({card}: {card: Card}) => {
  const [doUpdate] = useMutation("cards", "update");
  return (
    <DSButton
      size="sm"
      variant="secondary"
      onClick={() => doUpdate({id: card.cardId, visibility: "default"})}
    >
      Unarchive
    </DSButton>
  );
};

const TimeTrackingTooltip = ({segs}: {segs: TimeTrackingSegment[]}) => {
  return (
    <Col pa="12px" sp="8px">
      <Text type="label12" color="primary">
        Currently Tracking
      </Text>
      {segs.length === 0 ? (
        <Text type="label12" color="secondary">
          No one
        </Text>
      ) : (
        segs.map((seg) => (
          <Row key={seg.id} sp="12px" align="center">
            <Row align="center" sp="4px">
              <DSAvatar size={16} user={seg.user} />
              <Text type="label12" color="primary">
                {seg.user.name}
              </Text>
            </Row>
            <Text ml="auto" type="label12light" color="primary">
              {formatMsAsSec(getSegmentDuration(seg))}
            </Text>
          </Row>
        ))
      )}
    </Col>
  );
};

const TimeTrackingButton = ({root, card}: {root: Root; card: Card}) => {
  const cardId = card.$meta.get("cardId", null);
  const {result: segs, isLoaded} = checkIfPresent(
    () => (cardId ? getCurrentTimeTrackingSegmentsForCard(cardId, root) : []),
    api
  );
  const allowTimeTracking = root.account.timeTrackingMode === "strict";
  if (segs.length === 0 && !allowTimeTracking) return null;
  const meId = root.loggedInUser?.$meta.get("id", null);
  const meTracking = segs.find((seg) => seg.user?.id === meId);
  const getButtonProps = () => {
    if (meTracking) {
      return {
        variant: null,
        className: cx(
          cardStatusVariants.started,
          workflowStyles.nextButton.base,
          workflowStyles.nextButton.active
        ),
      };
    } else {
      return {
        variant: "tertiary",
        active: isLoaded && segs.length > 0,
      } as const;
    }
  };

  const handleClick = () => {
    if (meTracking) {
      api.mutate.timeTracking.stop({id: meTracking.id});
    } else {
      api.mutate.timeTracking.start({userId: meId, cardId});
    }
  };

  return (
    <TooltipForChild tooltip={<TimeTrackingTooltip segs={segs} />}>
      <DSIconButton
        negatePadding
        icon={
          meTracking ? (
            <AnimatedStopWatchIcon start={meTracking.startedAt} />
          ) : (
            <DSIconStopWatchPlay />
          )
        }
        skipSuccessIcon
        {...getButtonProps()}
        onClick={handleClick}
      />
    </TooltipForChild>
  );
};

const VisionBoardButton = ({card}: {card: Card}) => {
  const [doCreate] = useMutation("visionBoard", "createBoard");
  const visionBoardSeq = card.visionBoard?.$meta.get("accountSeq", null) ?? null;
  const cardId = card.$meta.get("cardId", null);
  const history = useHistory();
  const handleCreateClick = () =>
    confirm({title: "Create Vision Board for this Card"}).then((ok) => {
      if (ok && cardId)
        return doCreate({cardId}).then(({accountSeq}: any) => {
          history.push(`?visionBoard=${accountSeq}`);
        });
    });
  return (
    <TooltipForChild
      tooltip={visionBoardSeq ? "Open Vision Board" : "Create Vision Board"}
      placement="top"
    >
      <DSIconButton
        active={Boolean(visionBoardSeq)}
        {...(visionBoardSeq
          ? {to: `?visionBoard=${visionBoardSeq}`}
          : {onClick: handleCreateClick})}
        icon={<DSIconMisc />}
        variant={Boolean(visionBoardSeq) ? "primary" : "tertiary"}
        size="md"
        negatePadding
        skipSuccessIcon
      />
    </TooltipForChild>
  );
};

const TopRight = ({card, root, focusProps}: {card: Card; root: Root; focusProps: FocusProps}) => {
  const withVisionBoard = useFeatureFlag(root.account, FEATURE_FLAGS.visionBoard);
  const isSuperAdmin = root.loggedInUser && root.loggedInUser.cdxRole !== "none";

  return (
    <Row ml="auto" sp="12px">
      <TimeTrackingButton card={card} root={root} />
      <Upvotes card={card} root={root} />
      <PublicCardButton card={card} />
      <TooltipForChild tooltip="Enter focus mode" placement="top">
        <DSIconButton
          active={focusProps.active}
          onClick={() => focusProps.setActive()}
          icon={<DSIconFocusMode />}
          variant="tertiary"
          size="md"
          negatePadding
        />
      </TooltipForChild>
      {withVisionBoard && isSuperAdmin && <VisionBoardButton card={card} />}
    </Row>
  );
};

const PublicButtonArea = ({card}: {card: Card}) => {
  const status = getStatusForCard(card);
  switch (status) {
    case "started":
      return (
        <WorkflowButton
          tooltip="This Card is started."
          status="started"
          active
          icon={<DSIconPlay />}
        />
      );
    case "blocked":
      return (
        <WorkflowButton
          tooltip="This Card is blocked."
          status="blocked"
          active
          icon={<DSIconBlocked />}
        />
      );
    case "review":
      return (
        <WorkflowButton
          tooltip="This Card is in review."
          status="review"
          active
          icon={<DSIconReview />}
        />
      );
    case "snoozing":
      return (
        <WorkflowButton
          tooltip="This Card is snoozing."
          status="snoozing"
          active
          icon={<DSIconSnoozing />}
        />
      );
    case "done":
      return (
        <WorkflowButton tooltip="This Card is done." status="done" active icon={<DSIconCheck />} />
      );
    default:
      return null;
  }
};

type WorkflowProps = {
  card: Card;
  root: Root;
  onOpenChildCards: () => void;
  onCreateConvo: (convo: any) => void;
  canModify: boolean | null;
  focusProps: FocusProps;
  onOpenConvoPanel: () => void;
  onCardDone?: () => Promise<unknown>;
  coverInfo: CoverInfo | null;
};
const Workflow = (props: WorkflowProps) => {
  const {
    card,
    root,
    onOpenChildCards,
    onCreateConvo,
    canModify,
    focusProps,
    onOpenConvoPanel,
    onCardDone,
    coverInfo,
  } = props;
  const childCards = getChildCards(card);
  const isHero = isHeroCard(card);
  const mayGuardCard = hasPermissionToGuardCard(root, card);

  const getContent = () => {
    if (isHero) {
      return (
        <Row sp="16px" align="center">
          <ParentCardBadge childCardCount={childCards.length} onOpenChildCards={onOpenChildCards} />
          {card.visibility !== "archived" ? (
            <ParentDoneButton
              mayGuardCard={mayGuardCard}
              card={card}
              canModify={canModify}
              onCardDone={onCardDone}
              childCards={childCards}
            />
          ) : (
            canModify && <UnarchiveButton card={card} />
          )}
        </Row>
      );
    }

    if (card.visibility === "deleted") {
      return (
        <Text type="label14" color="secondary">
          Card has been deleted.
        </Text>
      );
    }

    if (card.visibility === "archived") {
      return (
        <Row sp="8px" align="center">
          <Text type="label14" color="secondary">
            Card is archived.
          </Text>
          {canModify && <UnarchiveButton card={card} />}
        </Row>
      );
    }

    if (card.isDoc) return null;

    if (process.env.REACT_APP_MODE === "open") return <PublicButtonArea card={card} />;

    return (
      <ButtonArea
        card={card}
        canModify={canModify}
        onCreateConvo={onCreateConvo}
        openConvoPanel={onOpenConvoPanel}
        onCardDone={onCardDone}
        mayGuardCard={mayGuardCard}
        root={root}
      />
    );
  };

  return (
    <Row
      align="center"
      className={cx(
        styles.container,
        !coverInfo || coverInfo.type === "white" ? colorThemes.gray50 : colorThemes.gray750,
        coverInfo
          ? coverInfo.type === "white"
            ? styles.withCoverLight
            : styles.withCoverDark
          : null
      )}
      bg={coverInfo ? undefined : "foreground"}
      sp="12px"
      px="12px"
      py="8px"
      flex="none"
      relative
      zIndex={1}
    >
      {getContent()}
      <TopRight card={card} root={root} focusProps={focusProps} />
    </Row>
  );
};

export default Workflow;
